import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Settings, Play, Download, Upload, Info, ChevronDown, ChevronRight, RotateCcw, BarChart3 } from 'lucide-react';
import ExtremeEventsResultsSubtab from './ExtremeEventsResultsSubtab';
import { 
    RISK_CATEGORIES, 
    EXTREME_EVENTS, 
    EXTERNAL_PARAMETERS,
    DEFAULT_EXTREME_EVENTS_CONFIG,
    getEventsByCategory,
    getEnabledEvents,
    getEventCounts,
    calculateExpectedAnnualLoss,
    validateConfiguration
} from '../../constants/extremeEventsConfig';
import { FEATURE_FLAGS } from '../../constants/appConfig';
import { logger } from '../../utils/logger';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { HybridMonteCarloSimulation } from '../../analytics/extremeEventsModeling';

const ExtremeEventsSubtab = ({ inputs, outputs, onRunSimulation, setInputs }) => {
    // Configuration state
    const [config, setConfig] = useState(DEFAULT_EXTREME_EVENTS_CONFIG);
    const [externalParams, setExternalParams] = useState(EXTERNAL_PARAMETERS);
    const [eventStates, setEventStates] = useState({});
    const [eventParameters, setEventParameters] = useState({});
    
    // UI state
    const [expandedCategories, setExpandedCategories] = useState({
        FINANCIAL_RISK: true,
        STRATEGIC_RISK: true,
        HAZARD_RISK: true,
        OPERATIONS_RISK: true,
        EXTREME_BLACK_SWAN: false
    });
    const [activeTab, setActiveTab] = useState('parameters'); // 'parameters' | 'events' | 'results'
    const [showAdvanced, setShowAdvanced] = useState(false);
    
    // Simulation state
    const [isRunning, setIsRunning] = useState(false);
    const [results, setResults] = useState(null);
    const [validationResult, setValidationResult] = useState({ isValid: true, errors: [], warnings: [] });
    const [simulationProgress, setSimulationProgress] = useState(0);

    // Initialize event states and parameters
    useEffect(() => {
        const initialEventStates = {};
        const initialEventParameters = {};
        Object.keys(EXTREME_EVENTS).forEach(categoryId => {
            EXTREME_EVENTS[categoryId].forEach(event => {
                initialEventStates[event.id] = event.enabled;
                initialEventParameters[event.id] = {
                    probability: event.probability,
                    impactRangeLow: event.impactRange[0],
                    impactRangeHigh: event.impactRange[1]
                };
            });
        });
        setEventStates(initialEventStates);
        setEventParameters(initialEventParameters);
        logger.info('Extreme events subtab initialized');
    }, []);

    // Sync external parameters mean values from main inputs
    useEffect(() => {
        const syncedParams = { ...externalParams };
        let hasChanges = false;

        Object.keys(EXTERNAL_PARAMETERS).forEach(paramId => {
            if (inputs[paramId] !== undefined && syncedParams[paramId]) {
                const currentMean = syncedParams[paramId].mean;
                const inputValue = inputs[paramId];
                
                if (currentMean !== inputValue) {
                    syncedParams[paramId] = {
                        ...syncedParams[paramId],
                        mean: inputValue
                    };
                    hasChanges = true;
                }
            }
        });

        if (hasChanges) {
            setExternalParams(syncedParams);
            logger.debug('External parameters synced from main inputs');
        }
    }, [inputs, externalParams]);

    // Validate configuration whenever it changes
    useEffect(() => {
        const validation = validateConfiguration(config);
        setValidationResult(validation);
    }, [config, eventStates]);

    // Update configuration when event states change
    useEffect(() => {
        const updatedConfig = { ...config };
        // Configuration updates are handled by individual handlers
        setConfig(updatedConfig);
    }, [eventStates]);

    const handleEventToggle = useCallback((eventId, enabled) => {
        setEventStates(prev => ({
            ...prev,
            [eventId]: enabled
        }));
        logger.debug(`Event ${eventId} ${enabled ? 'enabled' : 'disabled'}`);
    }, []);

    const handleEventParameterChange = useCallback((eventId, parameterType, value) => {
        setEventParameters(prev => ({
            ...prev,
            [eventId]: {
                ...prev[eventId],
                [parameterType]: parseFloat(value) || 0
            }
        }));
        logger.debug(`Event ${eventId} parameter ${parameterType} changed to ${value}`);
    }, []);

    const handleCategoryToggle = useCallback((categoryId, enabled) => {
        const updatedConfig = {
            ...config,
            riskCategories: {
                ...config.riskCategories,
                [categoryId]: {
                    ...config.riskCategories[categoryId],
                    enabled
                }
            }
        };
        setConfig(updatedConfig);

        // Toggle all events in category
        const categoryEvents = EXTREME_EVENTS[categoryId] || [];
        const newEventStates = { ...eventStates };
        categoryEvents.forEach(event => {
            newEventStates[event.id] = enabled;
        });
        setEventStates(newEventStates);

        logger.info(`Category ${categoryId} ${enabled ? 'enabled' : 'disabled'}`);
    }, [config, eventStates]);

    const handleParameterChange = useCallback((paramId, field, value) => {
        const numericValue = parseFloat(value) || 0;
        
        setExternalParams(prev => ({
            ...prev,
            [paramId]: {
                ...prev[paramId],
                [field]: numericValue
            }
        }));

        // If changing the mean value, also update the main inputs
        if (field === 'mean' && setInputs && inputs[paramId] !== undefined) {
            setInputs(prev => ({
                ...prev,
                [paramId]: numericValue
            }));
            logger.debug(`Main input ${paramId} updated to ${numericValue} from external parameters`);
        }
    }, [setInputs, inputs]);

    const handleConfigChange = useCallback((field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleRunSimulation = async () => {
        if (!validationResult.isValid) {
            logger.warn('Cannot run simulation with invalid configuration', validationResult.errors);
            return;
        }

        setIsRunning(true);
        setSimulationProgress(0);
        logger.info('Starting extreme events simulation', { config, enabledEvents: getEnabledEvents().length });

        try {
            // Prepare simulation configuration
            const simulationConfig = {
                ...config,
                externalParameters: externalParams,
                eventStates,
                eventParameters,
                riskCategories: Object.keys(RISK_CATEGORIES).reduce((acc, categoryId) => {
                    acc[categoryId] = { enabled: true };
                    return acc;
                }, {})
            };

            // Create and run simulation
            const simulation = new HybridMonteCarloSimulation(inputs, simulationConfig);
            
            // Progress tracking
            const progressInterval = setInterval(() => {
                setSimulationProgress(prev => Math.min(prev + 1, 95));
            }, 100);

            const simulationResults = await simulation.runSimulation();
            
            clearInterval(progressInterval);
            setSimulationProgress(100);
            
            setResults(simulationResults);
            setActiveTab('results');
            
            logger.info('Extreme events simulation completed successfully', {
                scenarios: simulationResults.scenarios?.length,
                extremeEvents: simulationResults.extremeEventResults?.length
            });
        } catch (error) {
            logger.error('Extreme events simulation failed', { error: error.message });
        } finally {
            setIsRunning(false);
            setTimeout(() => setSimulationProgress(0), 1000);
        }
    };

    const handleResetConfiguration = () => {
        setConfig(DEFAULT_EXTREME_EVENTS_CONFIG);
        setExternalParams(EXTERNAL_PARAMETERS);
        
        // Reset event states and parameters to defaults
        const defaultEventStates = {};
        const defaultEventParameters = {};
        Object.keys(EXTREME_EVENTS).forEach(categoryId => {
            EXTREME_EVENTS[categoryId].forEach(event => {
                defaultEventStates[event.id] = event.enabled;
                defaultEventParameters[event.id] = {
                    probability: event.probability,
                    impactRangeLow: event.impactRange[0],
                    impactRangeHigh: event.impactRange[1]
                };
            });
        });
        setEventStates(defaultEventStates);
        setEventParameters(defaultEventParameters);
        
        logger.info('Configuration reset to defaults');
    };

    const handleExportConfiguration = () => {
        const exportData = {
            config,
            externalParameters: externalParams,
            eventStates,
            eventParameters,
            timestamp: new Date().toISOString(),
            version: '1.0'
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `extreme-events-config-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        logger.info('Configuration exported');
    };

    const handleImportConfiguration = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (event) => {
            const file = event.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const importedData = JSON.parse(e.target.result);
                        
                        // Validate imported data structure
                        if (!importedData.config || !importedData.externalParameters || 
                            !importedData.eventStates || !importedData.eventParameters) {
                            throw new Error('Invalid configuration file format');
                        }
                        
                        // Apply imported configuration
                        setConfig(importedData.config);
                        setExternalParams(importedData.externalParameters);
                        setEventStates(importedData.eventStates);
                        setEventParameters(importedData.eventParameters);
                        
                        // Update main inputs if parameter means have changed
                        if (setInputs) {
                            const updatedInputs = { ...inputs };
                            Object.keys(EXTERNAL_PARAMETERS).forEach(paramId => {
                                if (inputs[paramId] !== undefined && importedData.externalParameters[paramId]) {
                                    updatedInputs[paramId] = importedData.externalParameters[paramId].mean;
                                }
                            });
                            setInputs(updatedInputs);
                        }
                        
                        logger.info('Configuration imported successfully', {
                            timestamp: importedData.timestamp,
                            version: importedData.version
                        });
                        
                        // Show success message (optional)
                        alert('Configuration imported successfully!');
                        
                    } catch (error) {
                        logger.error('Failed to import configuration', { error: error.message });
                        alert('Error importing configuration: ' + error.message);
                    }
                };
                reader.readAsText(file);
            }
        };
        input.click();
    };

    const renderParametersTab = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    External Parameters Configuration
                </h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-6">
                    <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                        <strong>🔄 Synchronized with Basic Data:</strong> Mean values automatically sync with Basic Data tab parameters.
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">
                        Changes in Basic Data → Updates means here | Changes in means here → Updates Basic Data
                    </p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Configure normal distributions for the 32 external market-driven parameters.
                    These parameters are outside investor control and subject to market volatility.
                </p>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {Object.entries(externalParams).map(([paramId, param]) => (
                        <div key={paramId} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <label className="font-medium text-gray-900 dark:text-white text-sm">
                                    {paramId}
                                </label>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {param.unit}
                                </span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                                {param.description}
                            </p>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                        Mean (μ) {inputs[paramId] !== undefined && 
                                            <span className="text-blue-500 dark:text-blue-400">🔄</span>
                                        }
                                    </label>
                                    <input
                                        type="number"
                                        value={param.mean}
                                        onChange={(e) => handleParameterChange(paramId, 'mean', e.target.value)}
                                        className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            inputs[paramId] !== undefined 
                                                ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-white' 
                                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                                        }`}
                                        step="any"
                                        title={inputs[paramId] !== undefined ? 'Synced with Basic Data tab' : 'Manual parameter'}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                        Std Dev (σ)
                                    </label>
                                    <input
                                        type="number"
                                        value={param.std}
                                        onChange={(e) => handleParameterChange(paramId, 'std', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md 
                                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        step="any"
                                        min="0"
                                    />
                                </div>
                            </div>
                            
                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                95% CI: [{(param.mean - 2 * param.std).toFixed(3)}, {(param.mean + 2 * param.std).toFixed(3)}]
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderEventsTab = () => (
        <div className="space-y-6">
            {Object.entries(RISK_CATEGORIES).map(([categoryId, category]) => (
                <div key={categoryId} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div 
                        className="p-4 cursor-pointer border-b border-gray-200 dark:border-gray-700"
                        onClick={() => setExpandedCategories(prev => ({
                            ...prev,
                            [categoryId]: !prev[categoryId]
                        }))}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2">
                                    {expandedCategories[categoryId] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                    <span className="text-2xl">{category.icon}</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {category.name}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {category.description}
                                    </p>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Power Law: k={category.powerLawParams.k}, α={category.powerLawParams.alpha}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center space-x-4">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    {getEventsByCategory(categoryId).filter(e => eventStates[e.id]).length} / {getEventsByCategory(categoryId).length} enabled
                                </div>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={config.riskCategories[categoryId]?.enabled || false}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleCategoryToggle(categoryId, e.target.checked);
                                        }}
                                        className="sr-only"
                                    />
                                    <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                                        config.riskCategories[categoryId]?.enabled 
                                            ? 'bg-blue-600' 
                                            : 'bg-gray-300 dark:bg-gray-600'
                                    }`}>
                                        <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform duration-200 ${
                                            config.riskCategories[categoryId]?.enabled 
                                                ? 'translate-x-6' 
                                                : 'translate-x-0.5'
                                        } mt-0.5`} />
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    {expandedCategories[categoryId] && (
                        <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {getEventsByCategory(categoryId).map(event => (
                                    <div 
                                        key={event.id} 
                                        className={`p-3 border rounded-lg transition-colors ${
                                            eventStates[event.id]
                                                ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
                                                : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                                                {event.name}
                                            </h4>
                                            <input
                                                type="checkbox"
                                                checked={eventStates[event.id] || false}
                                                onChange={(e) => handleEventToggle(event.id, e.target.checked)}
                                                className="ml-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </div>
                                        
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                            {event.description}
                                        </p>
                                        
                                        <div className="space-y-2 text-xs">
                                            <div>
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-gray-500 dark:text-gray-400">Probability:</span>
                                                    <span className="text-gray-400 dark:text-gray-500 text-xs">
                                                        1:{Math.round(1/(eventParameters[event.id]?.probability || event.probability))} years
                                                    </span>
                                                </div>
                                                <input
                                                    type="number"
                                                    value={eventParameters[event.id]?.probability || event.probability}
                                                    onChange={(e) => handleEventParameterChange(event.id, 'probability', e.target.value)}
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded 
                                                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                                             focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                                    step="0.001"
                                                    min="0"
                                                    max="1"
                                                    placeholder="0.000"
                                                />
                                            </div>
                                            <div>
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-gray-500 dark:text-gray-400">Impact Range:</span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-1">
                                                    <div>
                                                        <label className="block text-xs text-gray-400 dark:text-gray-500 mb-1">Low</label>
                                                        <input
                                                            type="number"
                                                            value={eventParameters[event.id]?.impactRangeLow || event.impactRange[0]}
                                                            onChange={(e) => handleEventParameterChange(event.id, 'impactRangeLow', e.target.value)}
                                                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded 
                                                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                                                     focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                                            step="0.01"
                                                            min="0"
                                                            max="1"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-gray-400 dark:text-gray-500 mb-1">High</label>
                                                        <input
                                                            type="number"
                                                            value={eventParameters[event.id]?.impactRangeHigh || event.impactRange[1]}
                                                            onChange={(e) => handleEventParameterChange(event.id, 'impactRangeHigh', e.target.value)}
                                                            className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded 
                                                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                                                     focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                                            step="0.01"
                                                            min="0"
                                                            max="1"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center">
                                                    {formatPercentage(eventParameters[event.id]?.impactRangeLow || event.impactRange[0])} - {formatPercentage(eventParameters[event.id]?.impactRangeHigh || event.impactRange[1])}
                                                </div>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 dark:text-gray-400">Severity:</span>
                                                <span className={`font-medium ${
                                                    event.severity === 'catastrophic' ? 'text-red-600 dark:text-red-400' :
                                                    event.severity === 'severe' ? 'text-orange-600 dark:text-orange-400' :
                                                    event.severity === 'moderate' ? 'text-yellow-600 dark:text-yellow-400' :
                                                    'text-green-600 dark:text-green-400'
                                                }`}>
                                                    {event.severity}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {event.philippinesContext && (
                                            <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-600 rounded text-xs">
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Philippines Context:</span>
                                                <div className="text-gray-600 dark:text-gray-400 mt-1">
                                                    {event.philippinesContext}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );

    const renderSimulationControls = () => (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Simulation Configuration
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Simulation Mode
                    </label>
                    <select
                        value={config.simulationMode}
                        onChange={(e) => handleConfigChange('simulationMode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="normal_only">Normal Variables Only</option>
                        <option value="extreme_only">Extreme Events Only</option>
                        <option value="hybrid">Normal + Extreme Events</option>
                    </select>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Scenarios
                    </label>
                    <input
                        type="number"
                        value={config.scenarios}
                        onChange={(e) => handleConfigChange('scenarios', parseInt(e.target.value) || 1000)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md 
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="100"
                        max="10000"
                        step="100"
                    />
                </div>
                
                <div className="flex items-center">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={config.correlationModeling}
                            onChange={(e) => handleConfigChange('correlationModeling', e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Correlation Modeling
                        </span>
                    </label>
                </div>
                
                <div className="flex items-center">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={config.cascadingEffects}
                            onChange={(e) => handleConfigChange('cascadingEffects', e.target.checked)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                            Cascading Effects
                        </span>
                    </label>
                </div>
            </div>
            
            {/* Validation Messages */}
            {!validationResult.isValid && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <div className="flex items-center">
                        <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                        <h4 className="text-sm font-medium text-red-800 dark:text-red-200">Configuration Errors</h4>
                    </div>
                    <ul className="mt-2 text-sm text-red-700 dark:text-red-300 list-disc list-inside">
                        {validationResult.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}
            
            {validationResult.warnings.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <div className="flex items-center">
                        <Info className="h-5 w-5 text-yellow-400 mr-2" />
                        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Warnings</h4>
                    </div>
                    <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                        {validationResult.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="flex flex-col">
                        <button
                            onClick={handleRunSimulation}
                            disabled={!validationResult.isValid || isRunning}
                            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 
                                     text-white rounded-md transition-colors duration-200"
                        >
                            <Play className="h-4 w-4 mr-2" />
                            {isRunning ? 'Running...' : 'Run Simulation'}
                        </button>
                        {isRunning && (
                            <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${simulationProgress}%` }}
                                />
                            </div>
                        )}
                    </div>
                    
                    <button
                        onClick={handleResetConfiguration}
                        className="flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 
                                 text-white rounded-md transition-colors duration-200"
                    >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset
                    </button>
                </div>
                
                <div className="flex items-center space-x-3">
                    <button
                        onClick={handleImportConfiguration}
                        className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 
                                 text-white rounded-md transition-colors duration-200"
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        Import Config
                    </button>
                    
                    <button
                        onClick={handleExportConfiguration}
                        className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 
                                 text-white rounded-md transition-colors duration-200"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Export Config
                    </button>
                </div>
            </div>
        </div>
    );

    const renderSummaryStats = () => {
        const eventCounts = getEventCounts();
        const enabledEvents = getEnabledEvents();
        const expectedLoss = calculateExpectedAnnualLoss();
        
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Configuration Summary
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {enabledEvents.length}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Enabled Events
                        </div>
                    </div>
                    
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {Object.values(eventCounts).reduce((sum, cat) => sum + cat.total, 0)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Total Events
                        </div>
                    </div>
                    
                    <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                            {formatPercentage(expectedLoss)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Expected Annual Loss
                        </div>
                    </div>
                    
                    <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {config.scenarios.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Scenarios
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (!FEATURE_FLAGS.ENABLE_EXTREME_EVENTS) {
        return (
            <div className="p-6 text-center">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Extreme Events Modeling Disabled
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                    This feature is currently disabled. Enable ENABLE_EXTREME_EVENTS in app configuration.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold mb-2">Extreme Events Modeling</h2>
                        <p className="text-red-100">
                            Power law distribution analysis of 101 extreme risk events with cascading effects modeling
                        </p>
                    </div>
                    <div className="text-6xl opacity-20">
                        🌋
                    </div>
                </div>
            </div>

            {/* Summary Statistics */}
            {renderSummaryStats()}

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                    {[
                        { id: 'parameters', name: 'External Parameters', icon: Settings },
                        { id: 'events', name: 'Extreme Events', icon: AlertTriangle },
                        { id: 'results', name: 'Results', icon: BarChart3, disabled: !results },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => !tab.disabled && setActiveTab(tab.id)}
                            disabled={tab.disabled}
                            className={`flex items-center px-1 py-4 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : tab.disabled 
                                        ? 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                        >
                            <tab.icon className="h-4 w-4 mr-2" />
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
                {activeTab === 'parameters' && renderParametersTab()}
                {activeTab === 'events' && renderEventsTab()}
                {activeTab === 'results' && results && (
                    <ExtremeEventsResultsSubtab 
                        simulationResults={results}
                        baselineResults={outputs}
                        isRunning={isRunning}
                        isDarkMode={false} // TODO: Pass from props
                    />
                )}
            </div>

            {/* Simulation Controls */}
            {renderSimulationControls()}

        </div>
    );
};

export default ExtremeEventsSubtab;